// Radha — enterprise AP-process tracking (migration 057): which customers pay
// on demand vs. run through a PO/GRN/vendor-portal process, and where each
// invoice sits in that process. tenant_id/set_by/updated_by are stamped
// server-side by triggers, never trusted from the client.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type PaymentChannel = "direct_pay" | "invoice_email" | "po_portal";
export type PoStatus = "not_issued" | "issued";
export type GrnStatus = "pending" | "partial" | "received";
export type PortalStatus = "not_submitted" | "submitted" | "under_review" | "approved" | "payment_scheduled" | "rejected";

export interface CustomerPaymentProfile {
  id: string; customer_name: string; payment_channel: PaymentChannel;
  ap_portal_name: string | null; payment_terms_days: number | null; notes: string | null;
}

export function useCustomerPaymentProfiles() {
  return useQuery({
    queryKey: ["customer-payment-profiles"],
    queryFn: async (): Promise<CustomerPaymentProfile[]> => {
      const { data, error } = await supabase
        .from("customer_payment_profiles")
        .select("id, customer_name, payment_channel, ap_portal_name, payment_terms_days, notes");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export function useSetPaymentChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { customer_name: string; payment_channel: PaymentChannel; ap_portal_name?: string; payment_terms_days?: number }) => {
      const { error: insErr } = await supabase.from("customer_payment_profiles").insert(input);
      if (!insErr) return;
      if (insErr.code !== "23505") throw insErr;
      const { error: updErr } = await supabase
        .from("customer_payment_profiles")
        .update({ payment_channel: input.payment_channel, ap_portal_name: input.ap_portal_name, payment_terms_days: input.payment_terms_days })
        .eq("customer_name", input.customer_name);
      if (updErr) throw updErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer-payment-profiles"] }),
  });
}

export interface InvoiceApStatus {
  id: string; invoice_id: string; po_number: string | null; po_status: PoStatus;
  grn_status: GrnStatus; portal_status: PortalStatus; portal_reference: string | null;
  expected_payment_date: string | null; notes: string | null; updated_at: string;
}

export function useInvoiceApStatuses() {
  return useQuery({
    queryKey: ["invoice-ap-status"],
    queryFn: async (): Promise<InvoiceApStatus[]> => {
      const { data, error } = await supabase
        .from("invoice_ap_status")
        .select("id, invoice_id, po_number, po_status, grn_status, portal_status, portal_reference, expected_payment_date, notes, updated_at");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

export type ApStatusInput = {
  invoice_id: string; po_number?: string | null; po_status?: PoStatus; grn_status?: GrnStatus;
  portal_status?: PortalStatus; portal_reference?: string | null; expected_payment_date?: string | null;
};

export function useSetApStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApStatusInput) => {
      const { error: insErr } = await supabase.from("invoice_ap_status").insert(input);
      if (!insErr) return;
      if (insErr.code !== "23505") throw insErr;
      const { invoice_id, ...rest } = input;
      const { error: updErr } = await supabase.from("invoice_ap_status").update(rest).eq("invoice_id", invoice_id);
      if (updErr) throw updErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice-ap-status"] }),
  });
}

export function apProcessErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}

/**
 * The channel actually in effect for a customer: an explicit staff decision
 * (a customer_payment_profiles row) always wins, in either direction. Absent
 * one, banding is by total invoiced revenue (not current outstanding, which
 * swings with payment timing and isn't a stable size signal) — see 057/058/059:
 *   < directPayMax        -> direct_pay     (card/UPI; small/individual accounts)
 *   >= poPortalMin         -> po_portal      (PO/GRN/portal process; large enterprise)
 *   in between             -> invoice_email  (emailed; customer pays on their own terms)
 */
export function effectivePaymentChannel(
  profile: CustomerPaymentProfile | undefined,
  totalInvoiced: number,
  directPayMax: number,
  poPortalMin: number,
): PaymentChannel {
  if (profile) return profile.payment_channel;
  if (totalInvoiced < directPayMax) return "direct_pay";
  if (totalInvoiced >= poPortalMin) return "po_portal";
  return "invoice_email";
}

